import { useTranslation } from 'react-i18next';
import { LogoIcon } from '@/assets/icons';
import { cn } from '@/libs/utils';
import { useThemeStore } from '@/store/useThemeStore';

/**
 * A loading animation component that optionally displays a logo and can be positioned using CSS.
 *
 * @param {Object} props - Component props.
 * @param {boolean} [props.showLogo=true] - Whether to display the logo in the loading animation.
 * @param {string} [props.className] - Optional additional CSS classes for styling the loading spinner.
 * @param {'fixed'|'absolute'} [props.position='fixed'] - The CSS position property for the loader, either 'fixed' or 'absolute'.
 * @param {boolean} [props.show=false] - Controls whether the loading animation is shown or hidden.
 * @returns {JSX.Element} The loading spinner component.
 */
export default function Loading({
  showLogo = true,
  className,
  position = 'fixed',
  show = false,
}) {
  const { t } = useTranslation(['tables']);

  const palette = useThemeStore((state) => state.palette);

  const positionClasses = cn({
    'fixed inset-0 min-h-screen bg-white': position === 'fixed',
    'absolute -inset-2 h-full m-2 rounded-[6px] bg-white/60 backdrop-blur-md':
      position === 'absolute',
  });

  if (!show) {
    return null;
  }

  return (
    <div
      className={cn(
        positionClasses,
        'z-[1002] grid items-center justify-center',
        className
      )}
    >
      <div className="relative grid items-center justify-center gap-4">
        <div className="flex flex-col items-center justify-center gap-3">
          {showLogo && <LogoIcon className="invert-[0.85]" />}

          <div className="flex animate-pulse justify-center space-x-2">
            <div
              style={{ backgroundColor: `rgb(${palette})` }}
              className={cn('h-4 w-4 rounded-full opacity-60')}
            />
            <div
              style={{ backgroundColor: `rgb(${palette})` }}
              className={cn('h-4 w-4 rounded-full opacity-60')}
            />
            <div
              style={{ backgroundColor: `rgb(${palette})` }}
              className={cn('h-4 w-4 rounded-full opacity-60')}
            />
          </div>
        </div>

        <span className="sr-only">{t('loading', { ns: 'tables' })}</span>
      </div>
    </div>
  );
}
